/**
 * KML writer — the reverse of kml.ts's reader, for the Export KML tool.
 *
 * Deliberately narrower than the reader. An import has to survive whatever
 * some other GIS wrote — arbitrary styles, nested folders, StyleMaps. An
 * export only has to describe features this app already has in hand:
 * geometry, and attributes as KML's own `<ExtendedData>` so they survive a
 * reimport into another GIS rather than only looking right in Google Earth's
 * info balloon. Renderer and label configuration do NOT travel — KML's
 * `<Style>` is a genuinely different language (ABGR colour order, icon scale,
 * line width in screen pixels) and translating this app's renderer into it
 * well enough to be worth trusting is a separate feature, not a detail of
 * getting the data out.
 */

import { labelOf } from '../components/tools/panels/featureLoad';

function escapeXml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** KML coordinates are `lon,lat[,alt]` — the same order GeoJSON already uses. */
function coordString(coord: number[]): string {
  return coord.length >= 2 && Number.isFinite(coord[0]) && Number.isFinite(coord[1])
    ? `${coord[0]},${coord[1]}`
    : '';
}

function ringToKml(ring: number[][]): string {
  return `<LinearRing><coordinates>${ring.map(coordString).join(' ')}</coordinates></LinearRing>`;
}

/** A GeoJSON polygon's rings are [outer, ...holes] — KML names the two roles instead of ordering them. */
function polygonToKml(rings: number[][][]): string {
  const [outer, ...holes] = rings;
  if (!outer) return '';
  const outerXml = `<outerBoundaryIs>${ringToKml(outer)}</outerBoundaryIs>`;
  const holesXml = holes.map((ring) => `<innerBoundaryIs>${ringToKml(ring)}</innerBoundaryIs>`).join('');
  return `<Polygon>${outerXml}${holesXml}</Polygon>`;
}

/** Empty string for anything with no coordinates to place — the caller drops those features. */
function geometryToKml(geometry: GeoJSON.Geometry | null | undefined): string {
  if (!geometry) return '';
  switch (geometry.type) {
    case 'Point':
      return `<Point><coordinates>${coordString(geometry.coordinates)}</coordinates></Point>`;
    case 'MultiPoint':
      return `<MultiGeometry>${geometry.coordinates
        .map((coord) => `<Point><coordinates>${coordString(coord)}</coordinates></Point>`)
        .join('')}</MultiGeometry>`;
    case 'LineString':
      return `<LineString><coordinates>${geometry.coordinates
        .map(coordString)
        .join(' ')}</coordinates></LineString>`;
    case 'MultiLineString':
      return `<MultiGeometry>${geometry.coordinates
        .map(
          (line) =>
            `<LineString><coordinates>${line.map(coordString).join(' ')}</coordinates></LineString>`,
        )
        .join('')}</MultiGeometry>`;
    case 'Polygon':
      return polygonToKml(geometry.coordinates);
    case 'MultiPolygon':
      return `<MultiGeometry>${geometry.coordinates.map(polygonToKml).join('')}</MultiGeometry>`;
    default:
      // GeometryCollection and anything else KML has no direct equivalent
      // for — dropped rather than guessed at.
      return '';
  }
}

function placemarkToKml(feature: GeoJSON.Feature, index: number): string {
  const geometryXml = geometryToKml(feature.geometry);
  if (!geometryXml) return '';

  const properties = (feature.properties ?? {}) as Record<string, unknown>;
  const dataXml = Object.entries(properties)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(
      ([key, value]) =>
        `<Data name="${escapeXml(key)}"><value>${escapeXml(
          typeof value === 'object' ? JSON.stringify(value) : String(value),
        )}</value></Data>`,
    )
    .join('');

  return (
    `<Placemark><name>${escapeXml(labelOf(feature, index))}</name>` +
    (dataXml ? `<ExtendedData>${dataXml}</ExtendedData>` : '') +
    `${geometryXml}</Placemark>`
  );
}

/**
 * A layer's features as one KML document. Features with no drawable geometry
 * (an attribute-only row, a malformed shape) are silently dropped — the same
 * trap `hasDrawableGeometry` guards against when this app DRAWS a layer, just
 * on the way out this time rather than in.
 */
export function featuresToKml(documentName: string, features: GeoJSON.Feature[]): string {
  const placemarks = features.map((feature, index) => placemarkToKml(feature, index)).join('');
  return (
    '<?xml version="1.0" encoding="UTF-8"?>' +
    '<kml xmlns="http://www.opengis.net/kml/2.2">' +
    `<Document><name>${escapeXml(documentName)}</name>${placemarks}</Document>` +
    '</kml>'
  );
}

/** How many placemarks a KML string produced — for the panel to report what actually exported. */
export function countPlacemarks(features: GeoJSON.Feature[]): number {
  return features.filter((feature) => geometryToKml(feature.geometry) !== '').length;
}

/** Triggers a browser download of the built KML text — no server round trip. */
export function downloadKml(fileName: string, kml: string): void {
  const url = URL.createObjectURL(
    new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = /\.kml$/i.test(fileName) ? fileName : `${fileName}.kml`;
  link.click();
  URL.revokeObjectURL(url);
}
